import {useCallback, useState, useMemo, useEffect} from 'react'
import {RepositoryAndIssuePicker, type PickerType} from '@github-ui/item-picker/RepositoryAndIssuePicker'
import type {IssuePickerItem} from '@github-ui/item-picker/IssuePicker'
import {graphql, useRelayEnvironment} from 'react-relay'
import {addBlockedByMutation} from '../../../mutations/add-blocked-by-mutation'
import {removeBlockedByMutation} from '../../../mutations/remove-blocked-by-mutation'
import type {
  DependenciesPickerBlockedByIssuesQuery,
  DependenciesPickerBlockedByIssuesQuery$data,
} from './__generated__/DependenciesPickerBlockedByIssuesQuery.graphql'
import {clientSideRelayFetchQueryRetained} from '@github-ui/relay-environment'
import styles from './DependenciesPicker.module.css'

type DependenciesPickerProps = {
  type: 'blockedBy'
  onClose?: () => void
  issueId: string
  organization: string
  defaultRepositoryNameWithOwner: string
  anchorRef: React.RefObject<HTMLElement>
}

export const DependenciesPickerBlockedByIssuesGraphqlQuery = graphql`
  query DependenciesPickerBlockedByIssuesQuery($id: ID!) {
    node(id: $id) {
      ... on Issue {
        id
        blockedBy(first: 100) {
          nodes {
            id
          }
        }
      }
    }
  }
`

export function DependenciesPicker({
  type,
  onClose,
  issueId,
  organization,
  defaultRepositoryNameWithOwner,
  anchorRef,
}: DependenciesPickerProps) {
  const environment = useRelayEnvironment()
  const [state, setState] = useState<'loading' | 'error' | 'success' | 'idle'>('idle')
  const [queryData, setQueryData] = useState<DependenciesPickerBlockedByIssuesQuery$data | null>(null)

  const fetchData = useCallback(async () => {
    setState('loading')
    clientSideRelayFetchQueryRetained<DependenciesPickerBlockedByIssuesQuery>({
      environment,
      query: DependenciesPickerBlockedByIssuesGraphqlQuery,
      variables: {id: issueId},
    }).subscribe({
      next: data => {
        setQueryData(data)
        setState('success')
      },
      error: () => {
        setState('error')
      },
    })
  }, [environment, issueId])

  useEffect(() => {
    if (state !== 'idle') return

    fetchData()
  }, [environment, fetchData, issueId, state])

  const [pickerType, setPickerType] = useState<PickerType>('Issue')

  // Process the fetched blockedBy issues into the format expected by the picker
  const blockedByIssueIds = useMemo(() => {
    if (!queryData?.node?.blockedBy?.nodes) return []

    return queryData.node.blockedBy.nodes.filter(issue => !!issue).map(issue => issue.id)
  }, [queryData])

  const onPickerTypeChange = useCallback(
    (t: PickerType) => {
      setPickerType(t)
      if (t === null && onClose) {
        onClose()
        setState('idle')
      }
    },
    [onClose],
  )

  const onIssueSelection = useCallback(
    (selectedIssues: IssuePickerItem[]) => {
      if (type === 'blockedBy') {
        // Compute the set difference between the currently selected issue IDs and the newly selected issue IDs
        const selectedIssueIds = new Set(selectedIssues.map(issue => issue.id))
        const blockedByIssueIdsSet = new Set(blockedByIssueIds)
        const issuesToAdd = Array.from(selectedIssueIds).filter(id => !blockedByIssueIdsSet.has(id))
        const issuesToRemove = Array.from(blockedByIssueIdsSet).filter(id => !selectedIssueIds.has(id))

        for (const blockingIssueId of issuesToAdd) {
          // Call the mutation to add the blockedBy relationship
          addBlockedByMutation({
            environment,
            input: {
              issueId,
              blockingIssueId,
            },
            onError: () => {
              // TODO: Implement graceful error handling.
            },
          })
        }

        for (const blockingIssueId of issuesToRemove) {
          // Call the mutation to remove the blockedBy relationship
          removeBlockedByMutation({
            environment,
            input: {
              issueId,
              blockingIssueId,
            },
            onError: () => {
              // TODO: Implement graceful error handling.
            },
          })
        }
      }
    },
    [environment, issueId, blockedByIssueIds, type],
  )

  return (
    <RepositoryAndIssuePicker
      anchorElement={props => {
        const {ref} = props as {
          ref: React.MutableRefObject<HTMLElement | null>
        }
        if (ref) {
          ref.current = anchorRef.current
        }
        return <></>
      }}
      onIssueSelection={onIssueSelection}
      organization={organization}
      defaultRepositoryNameWithOwner={defaultRepositoryNameWithOwner}
      pickerType={pickerType}
      onPickerTypeChange={onPickerTypeChange}
      selectedIssueIds={blockedByIssueIds}
      hiddenIssueIds={[issueId]}
      issuePickerProps={{
        selectionVariant: 'multiple',
        isLoading: state === 'loading',
        subtitle: 'Mark current issue as blocked by…',
        className: styles.DependenciesPicker,
        notice:
          state === 'error'
            ? {
                variant: 'error',
                text: "We couldn't load the blocking issues. Try reloading the page, or if the problem persists, contact support.",
              }
            : undefined,
      }}
    />
  )
}

try{ DependenciesPicker.displayName ||= 'DependenciesPicker' } catch {}